import {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Modal} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {ListService} from '../../../Domains/Service/Types/service';
import {ticketDetailPath} from '../../../Domains/Ticket/Helpers/ticketNavigation';
import {getTelephonyDetailsPathname} from '../../../Domains/Service/Helpers/ServiceHelper';
import {CustomerLight} from '../../../Domains/Auth/Types/customer';
import {BillSearchResponse} from '../../interfaces/bill';
import {TicketFromApi} from '../../interfaces/ticket';
import {formatAmount} from '../../common/tools/amount';
import {formatDate} from '../../common/tools/date';
import styles from './DashboardV2.module.css';

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const matchesAllFields = (value: unknown, query: string): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((v) => matchesAllFields(v, query));
  }
  return normalize(String(value)).includes(query);
};

const serviceRoute = (s: ListService): string => {
  if (s.type.includes('TELEPHONY')) return getTelephonyDetailsPathname(s.serviceId);
  // L'hébergement n'a pas de page détail unique : on renvoie vers le listing.
  if (s.type.some((t) => ['SERVER', 'BAY', 'DOMAIN', 'MAIL'].includes(t)))
    return '/services/hebergement';
  // Connexion : les liens 4G/LTE pointent vers leur consommation.
  if (s.is4g) return `/services/consommationLTE?cisId=${s.serviceId}`;
  return `/services/connexion/liens/${s.serviceId}`;
};

interface SearchColumnProps {
  title: string;
  icon: string;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}

const SearchColumn = ({
  title,
  icon,
  iconColor,
  count,
  children,
}: SearchColumnProps): JSX.Element => (
  <div className="col-12 col-lg">
    <div className="border rounded p-4 h-100 bg-light bg-opacity-25">
      <div className="d-flex align-items-center mb-4">
        <span
          className="d-flex align-items-center justify-content-center rounded me-3 flex-shrink-0"
          style={{width: 36, height: 36, backgroundColor: `${iconColor}1a`}}
        >
          <i className={`bi ${icon} fs-5`} style={{color: iconColor}} />
        </span>
        <span className="fw-bold fs-5 text-gray-900">{title}</span>
        <span
          className="badge ms-2"
          style={{backgroundColor: `${iconColor}1a`, color: iconColor, borderRadius: '50%'}}
        >
          {count}
        </span>
      </div>
      <div style={{maxHeight: 360, overflowY: 'auto'}}>{children}</div>
    </div>
  </div>
);

interface SearchModalProps {
  show: boolean;
  onHide: () => void;
  initialQuery: string;
  services: ListService[];
  bills: BillSearchResponse[];
  tickets: TicketFromApi[];
  customer: CustomerLight | undefined;
  showServices?: boolean;
  showBills?: boolean;
  showTickets?: boolean;
}

const SearchModal = ({
  show,
  onHide,
  initialQuery,
  services,
  bills,
  tickets,
  customer,
  showServices = true,
  showBills = true,
  showTickets = true,
}: SearchModalProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);

  // Resynchronise le champ avec le mot-clé du dashboard à chaque ouverture.
  useEffect(() => {
    if (show) setQuery(initialQuery);
  }, [show, initialQuery]);

  const normalized = normalize(query.trim());
  const hasQuery = normalized.length > 0;

  const matchedServices = useMemo(
    () => (hasQuery ? services.filter((s) => matchesAllFields(s, normalized)) : []),
    [services, normalized, hasQuery]
  );
  const matchedBills = useMemo(
    () => (hasQuery ? bills.filter((b) => matchesAllFields(b, normalized)) : []),
    [bills, normalized, hasQuery]
  );
  const matchedTickets = useMemo(
    () => (hasQuery ? tickets.filter((t) => matchesAllFields(t, normalized)) : []),
    [tickets, normalized, hasQuery]
  );

  const goTo = (to: string): void => {
    onHide();
    navigate(to);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">{intl.formatMessage({id: 'SEARCH.TITLE'})}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="position-relative mb-6">
          <i
            className="bi bi-search position-absolute text-gray-600 fs-5"
            style={{top: '50%', left: 18, transform: 'translateY(-50%)'}}
          />
          <input
            type="text"
            className="form-control bg-white border rounded-pill ps-12"
            placeholder={intl.formatMessage({id: 'SEARCH.PLACEHOLDER'})}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            style={{paddingTop: 12, paddingBottom: 12, fontSize: '1rem'}}
          />
        </div>

        {!hasQuery && (
          <div className="text-center text-muted py-10">
            {intl.formatMessage({id: 'SEARCH.HINT'})}
          </div>
        )}

        {hasQuery && (
          <div className="row g-8">
            {showServices && (
              <SearchColumn
                title={intl.formatMessage({id: 'SEARCH.SECTION.SERVICES'})}
                icon="bi-diagram-3"
                iconColor="#3699ff"
                count={matchedServices.length}
              >
                {matchedServices.length === 0 && (
                  <div className="text-muted fs-7 py-3">
                    {intl.formatMessage({id: 'SEARCH.NO_SERVICE'})}
                  </div>
                )}
                {matchedServices.map((s) => {
                  const location = s.site?.city || s.buName || s.link_name || null;
                  return (
                    <button
                      key={s.serviceId}
                      type="button"
                      onClick={() => goTo(serviceRoute(s))}
                      className={`btn btn-link text-start w-100 px-2 py-2 text-reset ${styles.softLink}`}
                    >
                      <div className="fw-semibold text-gray-900 text-truncate">{s.serviceName}</div>
                      <div className="text-muted fs-8">
                        {s.type.join(', ')}
                        {location ? ` · ${location}` : ''}
                      </div>
                    </button>
                  );
                })}
              </SearchColumn>
            )}

            {showBills && (
              <SearchColumn
                title={intl.formatMessage({id: 'SEARCH.SECTION.BILLS'})}
                icon="bi-receipt"
                iconColor="#7239ea"
                count={matchedBills.length}
              >
                {matchedBills.length === 0 && (
                  <div className="text-muted fs-7 py-3">
                    {intl.formatMessage({id: 'SEARCH.NO_BILL'})}
                  </div>
                )}
                {matchedBills.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => goTo('/bills')}
                    className={`btn btn-link text-start w-100 px-2 py-2 text-reset ${styles.softLink}`}
                  >
                    <div className="d-flex justify-content-between">
                      <span className="fw-semibold text-gray-900">{b.number}</span>
                      <span className="fw-bold text-gray-900">
                        {formatAmount(b.amountIncludingTax)}
                      </span>
                    </div>
                    <div className="text-muted fs-8">{formatDate(b.date)}</div>
                  </button>
                ))}
              </SearchColumn>
            )}

            {showTickets && (
              <SearchColumn
                title={intl.formatMessage({id: 'SEARCH.SECTION.TICKETS'})}
                icon="bi-ticket-perforated"
                iconColor="#f6a609"
                count={matchedTickets.length}
              >
                {matchedTickets.length === 0 && (
                  <div className="text-muted fs-7 py-3">
                    {intl.formatMessage({id: 'SEARCH.NO_TICKET'})}
                  </div>
                )}
                {matchedTickets.map((t) => (
                  <button
                    key={t.numDossier}
                    type="button"
                    onClick={() => goTo(ticketDetailPath(t, customer))}
                    className={`btn btn-link text-start w-100 px-2 py-2 text-reset ${styles.softLink}`}
                  >
                    <div className="fw-semibold text-gray-900 text-truncate">{t.titre}</div>
                    <div className="text-muted fs-8">
                      N° {t.numDossier}
                      {t.siteName ? ` · ${t.siteName}` : ''}
                    </div>
                  </button>
                ))}
              </SearchColumn>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SearchModal;
